import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigation } from '@react-navigation/native';
import { MaterialIcons } from '@expo/vector-icons';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Alert,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import * as yup from 'yup';
import type { RegisterRequest } from '../../api/types';
import { AuthShell, IconTextField, PrimaryCTA } from '../../components';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { fpColors } from '../../styles/theme';
import {
  clearError,
  registerUser,
  selectAuthError,
  selectAuthLoading,
  selectValidationErrors,
} from './authSlice';

const schema = yup.object({
  full_name: yup
    .string()
    .min(2, 'Name must be at least 2 characters')
    .required('Full name is required'),
  email: yup
    .string()
    .email('Please enter a valid email')
    .required('Email is required'),
  password: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .required('Password is required'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
});

interface RegisterFormData extends RegisterRequest {
  confirmPassword: string;
}

export const RegisterScreen: React.FC = () => {
  const navigation = useNavigation();
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectAuthLoading);
  const error = useAppSelector(selectAuthError);
  const validationErrors = useAppSelector(selectValidationErrors);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      full_name: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const clearOnType = () => {
    if (error || validationErrors) dispatch(clearError());
  };

  const onSubmit = async (data: RegisterFormData) => {
    dispatch(clearError());
    const { confirmPassword, ...registerData } = data;
    const result = await dispatch(registerUser(registerData));

    if (registerUser.rejected.match(result)) {
      const err = result.payload as any;
      const msg = err?.message?.toLowerCase() ?? '';
      const emailTaken =
        msg.includes('email') &&
        (msg.includes('exists') || msg.includes('registered') || msg.includes('taken'));
      if (emailTaken) {
        Alert.alert(
          'Email Already Registered',
          'This email address is already registered. Would you like to sign in instead?',
          [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Sign In', onPress: () => (navigation as any).navigate('Login') },
          ],
        );
      }
    }
    // On success, AppNavigator auto-routes based on requiresVerification state.
  };

  const errorText = (() => {
    if (typeof error === 'string' && error.length > 0) return error;
    if (validationErrors) {
      if (typeof validationErrors === 'string') return validationErrors;
      if (Array.isArray(validationErrors) && validationErrors.length > 0) {
        const first = validationErrors[0];
        return typeof first === 'string' ? first : (first as any)?.message ?? null;
      }
    }
    return null;
  })();

  return (
    <AuthShell showBack onBack={() => (navigation as any).navigate('Login')}>
      <View style={styles.headingBlock}>
        <Text style={styles.h1}>Create your account</Text>
        <Text style={styles.subtitle}>
          Your things, your people — shared on your terms.
        </Text>
      </View>

      <View style={styles.form}>
        <Controller
          control={control}
          name="full_name"
          render={({ field: { onChange, onBlur, value } }) => (
            <IconTextField
              label="Full Name"
              leftIcon="person"
              placeholder="Alex Sterling"
              value={value}
              onChangeText={(t) => {
                onChange(t);
                clearOnType();
              }}
              onBlur={onBlur}
              error={errors.full_name?.message}
              autoCapitalize="words"
              autoComplete="name"
              textContentType="name"
            />
          )}
        />

        <Controller
          control={control}
          name="email"
          render={({ field: { onChange, onBlur, value } }) => (
            <IconTextField
              label="Email Address"
              leftIcon="mail"
              placeholder="name@example.com"
              value={value}
              onChangeText={(t) => {
                onChange(t);
                clearOnType();
              }}
              onBlur={onBlur}
              error={errors.email?.message}
              keyboardType="email-address"
              autoCapitalize="none"
              autoComplete="email"
              textContentType="emailAddress"
            />
          )}
        />

        <Controller
          control={control}
          name="password"
          render={({ field: { onChange, onBlur, value } }) => (
            <IconTextField
              label="Password"
              leftIcon="lock"
              rightIcon={showPassword ? 'visibility-off' : 'visibility'}
              onRightIconPress={() => setShowPassword((v) => !v)}
              placeholder="At least 8 characters"
              value={value}
              onChangeText={(t) => {
                onChange(t);
                clearOnType();
              }}
              onBlur={onBlur}
              error={errors.password?.message}
              secureTextEntry={!showPassword}
              autoCapitalize="none"
              autoComplete="new-password"
              textContentType="newPassword"
            />
          )}
        />

        <Controller
          control={control}
          name="confirmPassword"
          render={({ field: { onChange, onBlur, value } }) => (
            <IconTextField
              label="Confirm Password"
              leftIcon="lock"
              rightIcon={showConfirm ? 'visibility-off' : 'visibility'}
              onRightIconPress={() => setShowConfirm((v) => !v)}
              placeholder="Re-enter password"
              value={value}
              onChangeText={(t) => {
                onChange(t);
                clearOnType();
              }}
              onBlur={onBlur}
              error={errors.confirmPassword?.message}
              secureTextEntry={!showConfirm}
              autoCapitalize="none"
              autoComplete="new-password"
              textContentType="newPassword"
            />
          )}
        />

        {errorText ? (
          <View style={styles.errorBanner}>
            <MaterialIcons name="error-outline" size={18} color={fpColors.error} />
            <Text style={styles.errorBannerText}>{errorText}</Text>
          </View>
        ) : null}

        <PrimaryCTA
          label="Create account"
          onPress={handleSubmit(onSubmit)}
          loading={isLoading}
        />

        <Text style={styles.microTrust}>
          Bank-grade encryption. You control who you share with.
        </Text>
      </View>

      <Text style={styles.footerText}>
        Already have an account?{' '}
        <Text
          style={styles.footerLink}
          onPress={() => (navigation as any).navigate('Login')}
        >
          Sign in
        </Text>
      </Text>
    </AuthShell>
  );
};

const styles = StyleSheet.create({
  headingBlock: {
    marginBottom: 16,
    alignItems: 'center',
  },
  h1: {
    fontFamily: 'Inter_700Bold',
    fontSize: 22,
    color: fpColors.onBackground,
    marginBottom: 4,
    textAlign: 'center',
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    lineHeight: 18,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
  },
  form: {
    gap: 12,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 12,
    borderRadius: 10,
    backgroundColor: fpColors.errorContainer,
  },
  errorBannerText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onErrorContainer,
  },
  microTrust: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
    marginTop: 2,
  },
  footerText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
    marginTop: 16,
  },
  footerLink: {
    fontFamily: 'Inter_700Bold',
    color: fpColors.primary,
  },
});
